<template>
  <div class="google-sign-in-button-component" :id="googleSignInButtonId"></div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useAuthStore } from '@tallo/auth';
import { handleError } from '@tallo/design-system';
import { useNavigation } from '@tallo/investor/navigation';

const props = defineProps<{ flow: 'sign-up' | 'sign-in' }>();
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const { openPortfolio, openCompanyOnboarding } = useNavigation();

const googleSignInButtonId = ref('tallo-investor-google-sign-in-button');

async function googleSignInCallback(googleJwt: string) {
  try {
    const companyId = await signInOrSignUp(googleJwt);

    navigateAfterAccountVerified(companyId);
  } catch (error) {
    handleError(error);
  }
}

function navigateAfterAccountVerified(companyId: string | null) {
  if (props.flow === 'sign-in' && route.redirectedFrom) {
    return router.push(route.redirectedFrom);
  }

  if (companyId) {
    openCompanyOnboarding({ companyId });
  } else {
    openPortfolio();
  }
}

async function signInOrSignUp(googleJwt: string): Promise<string | null> {
  if (props.flow === 'sign-up') {
    return (await authStore.investorSignUpWithGoogle(googleJwt)).companyId;
  } else {
    return (await authStore.investorSignInWithGoogle(googleJwt)).companyId;
  }
}

onMounted(() => {
  authStore.renderGoogleSignInButton({
    containerHtmlElementId: googleSignInButtonId.value,
    text: props.flow === 'sign-up' ? 'signup_with' : 'continue_with',
    callback: googleSignInCallback,
  });
});
</script>

<style scoped lang="scss">
.google-sign-in-button-component {
  width: 100%;
  height: 2.75rem;
}
</style>
